import { AsyncPipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { TextareaModule } from 'primeng/textarea';
import {
  BehaviorSubject,
  combineLatest,
  delay,
  EMPTY,
  filter,
  finalize,
  map,
  of,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  throttleTime,
  timer,
} from 'rxjs';
import { IMessage } from 'src/app/shared/interface/common';
import { SupportCase } from 'src/app/shared/models/support-case.model';
import { User } from 'src/app/shared/models/user.model';
import { ChatService } from 'src/app/shared/services/chat.service';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { UserInfoService } from 'src/app/shared/services/user-info.service';
import {
  WebSocketService,
  WS_MESSAGE_TYPE,
} from 'src/app/shared/services/web-socket.service';
import { AppConstant } from 'src/app/shared/utilities/app.constant';
import { AppHelper } from 'src/app/shared/utilities/app.helper';
import { transformMessage } from 'src/app/shared/utilities/data.transformer';
import { appendTimeSeriesFields } from 'src/app/shared/utilities/date-utils';
import { UserAvatarComponent } from '../../../shared/components/user-avatar/user-avatar.component';
import { TextInputValidate } from '../../../shared/directives/text-input-validate.directive';
import { ChatMessageComponent } from '../chat-message/chat-message.component';

@Component({
  selector: 'app-chat-box',
  templateUrl: './chat-box.component.html',
  styleUrls: ['./chat-box.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    UserAvatarComponent,
    ChatMessageComponent,
    ButtonModule,
    FileUploadModule,
    FormsModule,
    TextareaModule,
    TextInputValidate,
    AsyncPipe,
  ],
})
export class ChatBoxComponent implements OnInit, OnDestroy {
  listMessages: AppConstant.Message[] = [];
  messageInput: string = '';
  showError: boolean = false;
  defaultPageSize: number = 30;
  roleList = AppConstant.ROLES;
  showHasNewMessage: boolean = false;

  readonly isTyping$ = new BehaviorSubject<boolean>(false).asObservable();

  isScrollingUp: boolean = false;
  isShowConfirm: boolean = false; // Show confirmation dialog when needed

  currentSupportCaseId: number = -1;

  // Reactive state for typing indicator
  private readonly isAnotherTypingSubject = new BehaviorSubject<boolean>(false);
  readonly isAnotherTyping$ = this.isAnotherTypingSubject.asObservable();

  private readonly anotherTypingDataSubject = new BehaviorSubject<User | null>(
    null
  );
  readonly anotherTypingData$ = this.anotherTypingDataSubject.asObservable();

  // Combined observable for typing indicator - single subscription in template
  // Returns the typing user data only when someone is actively typing, otherwise null
  readonly typingIndicator$ = combineLatest([
    this.isAnotherTyping$,
    this.anotherTypingData$,
  ]).pipe(
    map(([isTyping, typingUser]) => {
      // Only show typing indicator if both conditions are met:
      // 1. isTyping flag is true
      // 2. typingUser data exists
      return isTyping && typingUser ? typingUser : null;
    })
  );

  isSelfTyping: boolean = false;
  private readonly typingSubject = new Subject<boolean>();
  private readonly typingIndicatorTrigger$ = new Subject<User | null>();
  private readonly sendMessageTrigger$ = new Subject<
    Partial<AppConstant.Message>
  >();
  userPreferredLanguage: string = '';

  private readonly destroy$ = new Subject<void>();
  private typingIndicatorStartTime: number | null = null;

  // Constants
  private readonly TYPING_INDICATOR_MIN_DISPLAY_MS = 1500; // 1.5 seconds minimum
  private readonly TYPING_INDICATOR_AUTO_HIDE_MS = 10000; // 10 seconds timeout
  private readonly SCROLL_UP_THRESHOLD = 150; // Pixels for scroll detection
  private readonly SCROLL_DELAY_MS = 100; // Delay before scrolling

  @ViewChild('fileUploader') fileUploader: FileUpload;

  @Input()
  currentUser: User;

  @Output()
  emitTotalUnread: EventEmitter<number> = new EventEmitter<number>();

  readonly isLoadingMessage$ = new BehaviorSubject<boolean>(false);

  @ViewChild('infiniteScroll') infiniteScroll?: ElementRef;

  private readonly onReceiveNewMessageBound: (
    message: AppConstant.Message
  ) => void;
  private readonly onReceiveMessageUpdatedBound: (
    message: AppConstant.Message
  ) => void;
  private readonly onReceiveFlagIsTypingBound: (wsMessage: any) => void;

  // Track chatbot status
  isChatbotEnabled: boolean = true;

  // Track if first message has been sent (to avoid multiple notifications)
  private firstMessageSent: boolean = false;

  constructor(
    private chatService: ChatService,
    private _notificationService: NotificationService,
    private _io: WebSocketService,
    private _userInfoService: UserInfoService,
    private cdr: ChangeDetectorRef
  ) {
    this.onReceiveNewMessageBound = this.onReceiveNewMessage.bind(this);
    this.onReceiveMessageUpdatedBound = this.onReceiveMessageUpdated.bind(this);
    this.onReceiveFlagIsTypingBound = this.onReceiveFlagIsTyping.bind(this);
  }

  ngOnInit() {
    this._io.registerMessageHanlder(
      WS_MESSAGE_TYPE.USER__NEW_MESSAGE,
      this.onReceiveNewMessageBound
    );
    this._io.registerMessageHanlder(
      WS_MESSAGE_TYPE.USER__MESSAGE_TRANSLATIONS_UPDATED,
      this.onReceiveMessageUpdatedBound
    );
    this._io.registerMessageHanlder(
      WS_MESSAGE_TYPE.LIVE_SUPPORT_AGENT__TYPING,
      this.onReceiveFlagIsTypingBound
    );
    this._io.registerMessageHanlder(
      WS_MESSAGE_TYPE.CHATBOT__TYPING,
      this.onReceiveFlagIsTypingBound
    );

    this.userPreferredLanguage =
      this._userInfoService.getUserInfoSubject()?.preferredLanguage;
    this.loadMessages();

    this.chatService
      .getCurrentSupportCase()
      .pipe(
        filter(supportCase => !!supportCase?.id),
        switchMap((supportCase: SupportCase) => {
          if (supportCase) {
            this.setCurrentSupportCaseId(supportCase.id);
            // Chatbot is now automatically enabled in backend when support case is retrieved
            this.isChatbotEnabled = supportCase.enabledChatbot;
          }
          const now = new Date().getTime();
          // Do not trigger the bot greeting when the user has disabled
          // chatbot replies on the current support case.
          if (supportCase && supportCase.enabledChatbot === false) {
            return of(null);
          }

          if (
            !supportCase ||
            supportCase.status === AppConstant.SupportCaseStatuses.RESOLVED
          ) {
            localStorage.setItem('lastGreetingTime', now.toString());
            return this.chatService.addEvent(
              AppConstant.SupportCaseEvents.USER__TRIGGER_GREETING
            );
          }

          const lastGreetingTime = localStorage.getItem('lastGreetingTime');
          if (this.shouldTriggerGreeting(lastGreetingTime, now)) {
            localStorage.setItem('lastGreetingTime', now.toString());
            return this.chatService.addEvent(
              AppConstant.SupportCaseEvents.USER__TRIGGER_GREETING
            );
          }
          return of(null);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.typingSubject
      .pipe(
        throttleTime(AppConstant.DELAY_TIME_SEND_STATUS_TYPING),
        tap(() => {
          try {
            this.sendEventTypingStatus();
          } catch (error) {
            console.error('Error in typing subscription:', error);
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    // Handle typing indicator with auto-hide timer
    this.typingIndicatorTrigger$
      .pipe(
        tap(user => {
          this.typingIndicatorStartTime = Date.now();
          this.isAnotherTypingSubject.next(!!user?.displayName);
          this.anotherTypingDataSubject.next(user);
        }),
        switchMap(() =>
          timer(this.TYPING_INDICATOR_AUTO_HIDE_MS).pipe(
            tap(() => this.clearTypingIndicator())
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe();

    // Handle message sending - send latest message immediately, cancel pending messages
    this.sendMessageTrigger$
      .pipe(
        tap(() => this.setLoadingMessageStatus(true)),
        switchMap(payload =>
          this.sendMessageAPI(payload).pipe(
            finalize(() => this.setLoadingMessageStatus(false))
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    // Complete all subjects for cleanup
    this.destroy$.next();
    this.destroy$.complete();

    // Unregister WebSocket handlers
    this._io.unregisterMessageHanlder(
      WS_MESSAGE_TYPE.USER__NEW_MESSAGE,
      this.onReceiveNewMessageBound
    );
    this._io.unregisterMessageHanlder(
      WS_MESSAGE_TYPE.USER__MESSAGE_TRANSLATIONS_UPDATED,
      this.onReceiveMessageUpdatedBound
    );
    this._io.unregisterMessageHanlder(
      WS_MESSAGE_TYPE.LIVE_SUPPORT_AGENT__TYPING,
      this.onReceiveFlagIsTypingBound
    );
    this._io.unregisterMessageHanlder(
      WS_MESSAGE_TYPE.CHATBOT__TYPING,
      this.onReceiveFlagIsTypingBound
    );

    // Complete subjects to prevent memory leaks
    this.typingSubject.complete();
    this.typingIndicatorTrigger$.complete();
    this.sendMessageTrigger$.complete();
    this.isLoadingMessage$.complete();
    this.isAnotherTypingSubject.complete();
    this.anotherTypingDataSubject.complete();
  }

  shouldTriggerGreeting(
    lastGreetingTime: string | null,
    currentTime: number
  ): boolean {
    if (!lastGreetingTime) {
      return true;
    }

    const lastTime = parseInt(lastGreetingTime, 10);
    const lastDate = new Date(lastTime);
    const currentDate = new Date(currentTime);

    const twoHours = 2 * 60 * 60 * 1000; // Two hours in milliseconds

    // Check if the last greeting was not sent on the same day
    if (
      lastDate.getDate() !== currentDate.getDate() ||
      lastDate.getMonth() !== currentDate.getMonth() ||
      lastDate.getFullYear() !== currentDate.getFullYear()
    ) {
      return true;
    }

    // Check if at least two hours have passed since the last greeting
    if (currentTime - lastTime >= twoHours) {
      return true;
    }
    return false;
  }

  onReceiveNewMessage(message: AppConstant.Message) {
    this.appendMessage(message);
    this.readNewMessage(message);
    this.scrollToBottomIfNotScrollingUp();
  }

  onReceiveMessageUpdated(message: AppConstant.Message) {
    const idx = this.listMessages.findIndex(
      (m: AppConstant.Message) => m.id === message.id
    );
    if (idx >= 0) {
      this.listMessages[idx] = {
        ...this.listMessages[idx],
        translations: message.translations,
      };
      // Trigger change detection after updating message
      this.cdr.markForCheck();
    }
  }

  onReceiveFlagIsTyping(wsMessage: {
    data?: any;
    toUser?: string | number;
    user?: User;
  }) {
    const eventData = wsMessage.data || wsMessage;
    const targetUserId = String(eventData.toUser);
    const currentUserId = String(this.currentUser?.id);

    if (targetUserId === currentUserId) {
      // Trigger typing indicator - switchMap will auto-cancel previous timer
      this.typingIndicatorTrigger$.next(eventData.user);
    }
  }

  loadMessages() {
    this.setLoadingMessageStatus(true);
    this.chatService
      .getMyMessages(this.defaultPageSize, this.listMessages.length)
      .pipe(tap(() => this.readAllMsg()))
      .subscribe((data: any) => {
        this.listMessages = [
          ...this.listMessages,
          ...(data.items || []).map((item: any) => transformMessage(item)),
        ];
        this.addTimeSeriesFields();
        this.setLoadingMessageStatus(false);
        // Trigger change detection after loading messages
        this.cdr.markForCheck();
      });
  }

  readAllMsg() {
    this.chatService.readAllLiveMessages().subscribe(() => {
      this.emitTotalUnread.emit(0);
    });
  }

  onScrollContainer(event: Event): void {
    if (!this.infiniteScroll) return;

    const scrollTop = Math.abs(this.infiniteScroll.nativeElement.scrollTop);
    const scrollHeight = this.infiniteScroll.nativeElement.scrollHeight;
    const offsetHeight = this.infiniteScroll.nativeElement.offsetHeight;

    // Check if user is scrolling up
    this.isScrollingUp = scrollTop > this.SCROLL_UP_THRESHOLD;

    if (scrollTop < 10) {
      this.showHasNewMessage = false;
    }

    // Load more messages when scrolled to bottom
    if (scrollHeight <= offsetHeight + scrollTop + 1) {
      this.onScrollUp();
    }
  }

  onScrollUp() {
    if (this.isLoadingMessage$.getValue()) {
      return;
    }
    this.loadMessages();
  }

  scrollToBottomIfNotScrollingUp() {
    if (this.isScrollingUp) return;
    this.scrollToBottom();
  }

  scrollToBottom() {
    if (!this.infiniteScroll) return;

    of(null)
      .pipe(delay(this.SCROLL_DELAY_MS), takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.infiniteScroll) {
          this.infiniteScroll.nativeElement.scrollTop =
            this.infiniteScroll.nativeElement.scrollHeight;
          this.showHasNewMessage = false;
        }
      });
  }

  onSend() {
    if (!this.messageInput || this.messageInput.trim() === '') {
      this.showError = true;
      this.messageInput = '';
      return;
    }

    this.showError = false;
    const trimmedMessage = this.messageInput.trim();

    this.sendMessageTrigger$.next({
      content: trimmedMessage,
      type: AppConstant.MessageTypes.TEXT,
      senderType: AppConstant.SenderTypes.USER,
    });
    this.messageInput = '';
  }

  /**
   * Sends a message and handles special scenarios:
   * - First message when chatbot is inactive/disabled
   *
   * @param payload The message to send
   */
  sendMessageAPI(payload: Partial<AppConstant.Message>) {
    // Check if this is first message with chatbot inactive
    const shouldSendWebhook = !this.firstMessageSent && !this.isChatbotEnabled;
    const userMessage =
      typeof payload.content === 'string' ? payload.content : '';

    return this.chatService.sendMessage(payload).pipe(
      tap((data: AppConstant.Message) => {
        this.removeLoadingMsg();

        if (this.currentSupportCaseId !== data.supportCaseId) {
          this.setCurrentSupportCaseId(data.supportCaseId);
        }

        this.appendMessage(data);
        this.scrollToBottom();

        // Set flag to indicate a message has been sent - used to control notification logic for chatbot inactivity
        this.firstMessageSent = true;
      }),
      switchMap((data: AppConstant.Message) => {
        // Handle agent notifications based on message context
        if (data.isNewTicket) {
          return this._userInfoService.userSubject.pipe(
            filter(userInfo => !!userInfo?.id),
            take(1),
            switchMap(userInfo =>
              this.chatService.sendAgentNotification(
                userInfo,
                'User Create a new ticket!',
                userMessage
              )
            )
          );
        } else if (shouldSendWebhook) {
          return this._userInfoService.userSubject.pipe(
            filter(userInfo => !!userInfo?.id),
            take(1),
            switchMap(userInfo =>
              this.chatService.sendAgentNotification(
                userInfo,
                'User sent a message while chatbot is inactive',
                userMessage
              )
            )
          );
        }
        return EMPTY; // No notification needed
      })
    );
  }

  createLoadingMsg() {
    this.listMessages.splice(0, 0, {
      id: -1,
      supportCaseId: -1,
      type: AppConstant.MessageTypes.TEXT,
      senderType: AppConstant.SenderTypes.USER,
      content: '',
      userId: this.currentUser.id,
      chatId: '',
      userName: this.currentUser.displayName,
      avatar: '',
      title: '',
      timestamp: new Date(),
      attachments: [],
      isInterationDisable: false,
    });
    this.scrollToBottom();
  }

  removeLoadingMsg() {
    const idx = this.listMessages.findIndex(
      (m: AppConstant.Message) => m.id === -1
    );
    if (idx >= 0) {
      this.listMessages.splice(idx, 1);
      // Trigger change detection after removing loading message
      this.cdr.markForCheck();
    }
  }

  appendMessage(message: AppConstant.Message) {
    // Prevent duplicate messages
    const messageAlreadyExists = this.listMessages.some(
      m => m.id === message.id
    );
    if (messageAlreadyExists) return;

    // Only apply typing indicator delay for bot/agent messages, not user messages
    const isUserMessage = message.senderType === AppConstant.SenderTypes.USER;

    if (isUserMessage) {
      // User messages should appear immediately without delay
      this.clearTypingIndicator();
      this.addMessageToList(message);
      return;
    }

    // For bot/agent messages, calculate delay for typing indicator visibility
    const delay = this.calculateTypingIndicatorDelay();

    if (delay) {
      // Delay required to maintain typing indicator minimum visibility
      timer(delay)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.clearTypingIndicator();
          this.addMessageToList(message);
        });
    } else {
      // No delay needed - add message immediately for best performance
      this.clearTypingIndicator();
      this.addMessageToList(message);
    }
  }

  /**
   * Calculates the delay needed before displaying a message to ensure
   * the typing indicator is visible for minimum required time.
   * @returns Delay in milliseconds (0 if no delay needed)
   */
  private calculateTypingIndicatorDelay(): number {
    const isTyping = this.isAnotherTypingSubject.getValue();
    if (!isTyping || !this.typingIndicatorStartTime) {
      return 0; // No typing indicator active, no delay needed
    }

    const elapsedTime = Date.now() - this.typingIndicatorStartTime;
    return Math.max(0, this.TYPING_INDICATOR_MIN_DISPLAY_MS - elapsedTime);
  }

  /**
   * Clears the typing indicator state
   */
  private clearTypingIndicator(): void {
    if (this.isAnotherTypingSubject.getValue()) {
      this.isAnotherTypingSubject.next(false);
      this.anotherTypingDataSubject.next(null);
      this.typingIndicatorStartTime = null;
    }
  }

  private addMessageToList(message: AppConstant.Message) {
    this.listMessages.unshift(transformMessage(message));
    this.addTimeSeriesFields();

    // Trigger change detection for OnPush strategy
    this.cdr.markForCheck();

    if (
      this.isScrollingUp &&
      (message.senderType === AppConstant.SenderTypes.LIVE_AGENT ||
        message.senderType === AppConstant.SenderTypes.BOT)
    ) {
      this.showHasNewMessage = true;
    }
  }

  addTimeSeriesFields() {
    this.listMessages = appendTimeSeriesFields(this.listMessages, 'timestamp');
  }

  readNewMessage(message: AppConstant.Message) {
    this.chatService.readLiveMessage(message.id).subscribe(() => {});
  }

  onInputFocus() {
    this.showError = false;
  }

  onKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      this.onSend();
      event.preventDefault();
    }
  }

  onChange(event: Event) {
    try {
      const target = event.target as HTMLTextAreaElement;
      const isTyping = !!target?.value;

      // Only emit if typingSubject is not closed
      if (!this.typingSubject.closed) {
        this.typingSubject.next(isTyping);
      }
    } catch (error) {
      console.error('Error in onChange:', error);
      // Fallback to ensure input continues working
    }
  }

  onClickNewMessages() {
    this.scrollToBottom();
  }

  selectFile() {
    this.fileUploader.choose();
  }

  onSelect(event: { currentFiles: File[] }): void {
    const filesList = event.currentFiles;
    const errors: IMessage[] = [];

    // Validate all files
    const validFiles = this.validateFiles(filesList, errors);

    // Show all errors
    errors.forEach(error => this._notificationService.setMessage(error));

    // Send valid files
    if (validFiles.length > 0) {
      this.createLoadingMsg();
      this.sendMessageTrigger$.next({
        content: '',
        type: AppConstant.MessageTypes.ATTACHMENT,
        senderType: AppConstant.SenderTypes.USER,
        attachments: validFiles,
      });
    }

    this.fileUploader.clear();
  }

  /**
   * Validates files and returns only valid ones
   */
  private validateFiles(files: File[], errors: IMessage[]): File[] {
    const MAXIMUM_FILES = 5;
    const MAXIMUM_TOTAL_SIZE = 10485760; // 10MB
    let currentTotalSize = 0;
    const validFiles: File[] = [];

    for (const file of files) {
      // Stop if we already have max files
      if (validFiles.length >= MAXIMUM_FILES) {
        errors.push({
          type: AppConstant.MESSAGE_TYPE.WARNING,
          header: 'Exceeded allowed number of files',
          content: `Maximum ${MAXIMUM_FILES} files are allowed`,
        });
        break;
      }

      // Validate file type
      if (!this.isValidFileType(file)) {
        errors.push({
          type: AppConstant.MESSAGE_TYPE.WARNING,
          header: 'Invalid File Type',
          content:
            'Invalid file type. Allowed file types: .pdf,.jpg, .jpeg, .png',
        });
        continue;
      }

      // Validate file name
      if (!this.isValidFileName(file.name)) {
        errors.push({
          type: AppConstant.MESSAGE_TYPE.WARNING,
          header: 'Detected: Invalid file name',
          content:
            "A file name can't contain any of the following characters: ! ' \" { } [ ] ( ) \\ / : ? < > | ",
        });
        continue;
      }

      // Validate file size
      if (file.size === 0) {
        errors.push({
          type: AppConstant.MESSAGE_TYPE.WARNING,
          header: 'Invalid file size',
          content: 'Empty file cannot be uploaded.',
        });
        continue;
      }

      if (file.size > MAXIMUM_TOTAL_SIZE) {
        errors.push({
          type: AppConstant.MESSAGE_TYPE.WARNING,
          header: 'Invalid file size',
          content: 'Allowed maximum file size: 10MB',
        });
        continue;
      }

      // Validate total size
      if (currentTotalSize + file.size > MAXIMUM_TOTAL_SIZE) {
        errors.push({
          type: AppConstant.MESSAGE_TYPE.WARNING,
          header: 'Invalid total file size',
          content: 'File exceeded total allowed file size. Should be removed',
        });
        continue;
      }

      // File is valid
      validFiles.push(file);
      currentTotalSize += file.size;
    }

    return validFiles;
  }

  /**
   * Checks if file type is allowed
   */
  private isValidFileType(file: File): boolean {
    const allowedTypes = [
      'image/png',
      'image/jpeg',
      'image/jpg',
      'application/pdf',
    ];
    return allowedTypes.includes(file.type.toLowerCase());
  }

  /**
   * Checks if file name contains invalid characters
   */
  private isValidFileName(fileName: string): boolean {
    const invalidCharsRegex = /[!()\[\]{}\\|<>\/?'"]+/;
    const extractedName = AppHelper.StringFunctions.extractFileName(fileName);
    return !invalidCharsRegex.test(extractedName);
  }

  setCurrentSupportCaseId(newId: number) {
    this.currentSupportCaseId = newId;
  }

  sendEventTypingStatus() {
    if (this.currentSupportCaseId < 0) return;

    try {
      const data = {
        type: WS_MESSAGE_TYPE.USER__TYPING,
        data: {
          supportCaseId: this.currentSupportCaseId,
          user: this.currentUser,
        },
      };
      this._io.emit(`message`, data);
    } catch (error) {
      console.error('Error sending typing status:', error);
    }
  }

  setLoadingMessageStatus(value: boolean) {
    this.isLoadingMessage$.next(value);
  }
}
